import { history } from "@umijs/max";
import { useSession } from "@zhiyue/core";
import { Button, Result, Spin } from "antd";
import React from "react";
import { OpenPlatformAccessTokenContext } from "@/contexts/openPlatform";
import {
  clearOpenPlatformToken,
  fetchOpenPlatformToken,
  type OpenPlatformTokenResult,
} from "@/services/openPlatformToken";

const publicPaths = ["/user/login", "/callback"];
const TOKEN_REFRESH_BUFFER_MS = 60_000;

const normalizeText = (value: unknown) => String(value ?? "").trim();

const OpenPlatformBootstrap: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { currentUser, loading } = useSession();
  const [readyKey, setReadyKey] = React.useState("");
  const [errorKey, setErrorKey] = React.useState("");
  const [retryCount, setRetryCount] = React.useState(0);
  const [tokenInfo, setTokenInfo] =
    React.useState<OpenPlatformTokenResult | null>(null);

  const organizationUuid = normalizeText(
    (currentUser as (typeof currentUser & { organizationUuid?: string }) | null)
      ?.organizationUuid
  );
  const memberUuid = normalizeText(currentUser?.memberUuid);
  const userKey = [organizationUuid, memberUuid].join("|");
  const identityKey = currentUser ? userKey || "invalid-session" : "";
  const hasCurrentUser = Boolean(currentUser);
  const bootstrapUser = React.useMemo(
    () =>
      hasCurrentUser
        ? {
            organizationUuid,
            memberUuid,
          }
        : null,
    [hasCurrentUser, organizationUuid, memberUuid]
  );
  const isPublicPath = publicPaths.includes(history.location.pathname);

  React.useEffect(() => {
    if (loading || isPublicPath) {
      return;
    }

    if (
      !hasCurrentUser ||
      !bootstrapUser?.organizationUuid ||
      !bootstrapUser.memberUuid
    ) {
      clearOpenPlatformToken();
      setReadyKey("");
      setErrorKey(hasCurrentUser ? identityKey : "");
      setTokenInfo(null);
      return;
    }

    let cancelled = false;
    setErrorKey("");

    void fetchOpenPlatformToken(bootstrapUser)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setTokenInfo(result);
        setReadyKey(identityKey);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setTokenInfo(null);
        setReadyKey("");
        setErrorKey(identityKey);
      });

    return () => {
      cancelled = true;
    };
  }, [
    bootstrapUser,
    hasCurrentUser,
    identityKey,
    isPublicPath,
    loading,
    retryCount,
  ]);

  React.useEffect(() => {
    if (
      !bootstrapUser ||
      readyKey !== identityKey ||
      !tokenInfo ||
      !hasCurrentUser
    ) {
      return;
    }

    const refreshDelay = Math.max(
      tokenInfo.expiresAt - Date.now() - TOKEN_REFRESH_BUFFER_MS,
      0
    );
    const timer = window.setTimeout(() => {
      clearOpenPlatformToken();
      void fetchOpenPlatformToken(bootstrapUser)
        .then((result) => {
          setTokenInfo(result);
          setReadyKey(identityKey);
          setErrorKey("");
        })
        .catch(() => {
          setTokenInfo(null);
          setReadyKey("");
          setErrorKey(identityKey);
        });
    }, refreshDelay);

    return () => window.clearTimeout(timer);
  }, [bootstrapUser, hasCurrentUser, identityKey, readyKey, tokenInfo]);

  if (isPublicPath || (!loading && !hasCurrentUser)) {
    return (
      <OpenPlatformAccessTokenContext.Provider value="">
        {children}
      </OpenPlatformAccessTokenContext.Provider>
    );
  }

  if (errorKey === identityKey && identityKey) {
    return (
      <Result
        status="error"
        title="应用初始化失败"
        extra={
          <Button
            type="primary"
            onClick={() => {
              clearOpenPlatformToken();
              setReadyKey("");
              setErrorKey("");
              setTokenInfo(null);
              setRetryCount((value) => value + 1);
            }}
          >
            重试
          </Button>
        }
      />
    );
  }

  if (loading || readyKey !== identityKey || !tokenInfo) {
    return <Spin spinning size="large" fullscreen />;
  }

  return (
    <OpenPlatformAccessTokenContext.Provider value={tokenInfo.accessToken}>
      {children}
    </OpenPlatformAccessTokenContext.Provider>
  );
};

export default OpenPlatformBootstrap;
